import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import defaultCalendarOptions, {
  calendarEventDateTime,
  calendarEventTimeFormat,
  calendarTimeZone,
  calendarZonedDateParts,
  currentScrollTime,
  renderCalendarCentered
} from "@appComponents/helpers/full-calendar-default-config"
import FlashMessage from "src/shared/components/flash-messages"
import { loadFullCalendar } from "@appComponents/helpers/load_full_calendar"

const AVAILABILITIES_ENDPOINT = "/api/v1/availabilities"
const DEFAULT_EVENT_COLOR = "#3788d8"

// Set once FullCalendar and Moment are loaded (connect).
let Calendar, timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, frLocale, moment

export default class extends Controller {
  static targets = [
    "calendar",
    "availabilityModalTitle",
    "availabilityErrors",
    "availabilityDate",
    "startTime",
    "endTime",
    "appointmentsNumber",
    "practitionerOfficeId",
    "deleteAvailabilityButton",
    "submitAvailabilityButton"
  ]

  static values = {
    practitionerId: Number,
    appointmentDuration: Number,
    availabilityModalId: String,
    availabilityEventTitle: String,
    newAvailabilityTitle: String,
    editAvailabilityTitle: String,
    createActionLabel: String,
    updateActionLabel: String,
    createdFlashMessage: String,
    updatedFlashMessage: String,
    deletedFlashMessage: String
  }

  async connect () {
    this.availabilities = []
    this.currentAvailability = null
    this.editing = false

    const connection = (this.connection = Symbol("connection"))
    const calendar = await loadFullCalendar()
    if (this.connection !== connection) return // disconnected (or reconnected) while loading
    ;({ Calendar, timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin, frLocale, moment } = calendar)

    this.initializeCalendar()
  }

  disconnect () {
    this.connection = null
    this.calendar?.destroy()
  }

  initializeCalendar () {
    this.calendar = new Calendar(this.calendarTarget, {
      ...defaultCalendarOptions,
      plugins: [timeZonePlugin, dayGridPlugin, timeGridPlugin, listPlugin, interactionPlugin],
      locale: frLocale,
      timeZone: calendarTimeZone(),
      eventTimeFormat: calendarEventTimeFormat,
      scrollTime: currentScrollTime(),
      selectable: true,
      select: (info) => this.openNewAvailability(info),
      eventClick: (info) => this.openExistingAvailability(info),
      events: (fetchInfo, successCallback, failureCallback) => {
        this.fetchAvailabilities(fetchInfo.startStr, fetchInfo.endStr)
          .then((events) => successCallback(events))
          .catch((error) => failureCallback(error))
      }
    })

    renderCalendarCentered(this.calendar, this.calendarTarget)
  }

  async fetchAvailabilities (startAt, endAt) {
    const response = await fetch(`${AVAILABILITIES_ENDPOINT}?${new URLSearchParams({
      start_at: startAt,
      end_at: endAt,
      practitioner_id: this.practitionerIdValue
    })}`, {
      headers: { Accept: "application/json" },
      credentials: "same-origin"
    })

    const data = await response.json()
    this.availabilities = data.data || []

    return this.buildEvents()
  }

  buildEvents () {
    return this.availabilities.map((availability) => {
      const attributes = availability.attributes
      const isPast = moment(attributes.start_at).isBefore(moment())

      const color = attributes.color || DEFAULT_EVENT_COLOR

      return {
        id: availability.id,
        title: attributes.patient_full_name || this.availabilityEventTitleValue,
        start: calendarEventDateTime(attributes.start_at),
        end: calendarEventDateTime(attributes.end_at),
        extendedProps: { availability, isPast },
        backgroundColor: "transparent",
        borderColor: color,
        classNames: ["fc-event--availability", isPast ? "fc-event--past" : ""].filter(Boolean)
      }
    })
  }

  openNewAvailability (selectionInfo) {
    this.editing = false
    this.currentAvailability = this.newAvailabilityPayload()

    const start = calendarZonedDateParts(selectionInfo.startStr || selectionInfo.start)
    const end = calendarZonedDateParts(selectionInfo.endStr || selectionInfo.end)

    this.currentAvailability.attributes.date = start.date
    this.currentAvailability.attributes.start_time = start.time
    this.currentAvailability.attributes.end_time = end.time

    if (this.hasPractitionerOfficeIdTarget &&
        !this.currentAvailability.attributes.practitioner_office_id &&
        this.practitionerOfficeIdTarget.value) {
      this.currentAvailability.attributes.practitioner_office_id = this.practitionerOfficeIdTarget.value
    }

    this.calendar.unselect()
    this.showAvailabilityModalWithForm()
  }

  openExistingAvailability (eventInfo) {
    const availability = eventInfo.event.extendedProps.availability
    if (!availability) return

    this.editing = true
    this.currentAvailability = structuredClone(availability)
    this.showAvailabilityModalWithForm()
  }

  showAvailabilityModalWithForm () {
    const modalElement = document.getElementById(this.availabilityModalIdValue)
    if (!modalElement) return

    if (this.hasAvailabilityDateTarget) {
      modalElement.addEventListener("shown.bs.modal", () => {
        this.populateAvailabilityForm()
      }, { once: true })
    } else if (this.hasAvailabilityModalTitleTarget) {
      modalElement.addEventListener("shown.bs.modal", () => {
        this.updateAvailabilityTitle()
      }, { once: true })
    }

    Modal.getOrCreateInstance(modalElement).show()
  }

  populateAvailabilityForm () {
    if (!this.hasAvailabilityDateTarget) return

    const attributes = this.currentAvailability.attributes

    if (this.hasAvailabilityErrorsTarget) {
      this.clearErrors(this.availabilityErrorsTarget)
    }
    this.setAvailabilityDate(attributes.date)
    this.startTimeTarget.value = attributes.start_time || ""
    this.endTimeTarget.value = attributes.end_time || ""
    this.appointmentsNumberTarget.value = attributes.appointments_number || 1

    if (attributes.practitioner_office_id) {
      this.practitionerOfficeIdTarget.value = attributes.practitioner_office_id
    } else if (this.practitionerOfficeIdTarget.options?.length) {
      this.practitionerOfficeIdTarget.selectedIndex = 0
    }

    this.updateAvailabilityTitle()
    this.updateAvailabilityActions()
    this.updateEndTimeFromDuration()
  }

  setAvailabilityDate (value) {
    const input = this.availabilityDateTarget
    if (!input) return

    const flatpickrInstance = input._flatpickr

    if (!value) {
      input.value = ""
      flatpickrInstance?.clear()
      return
    }

    const parsedDate = moment(value, ["DD/MM/YYYY", "D/M/YYYY"], true)
    if (!parsedDate.isValid()) return

    if (flatpickrInstance) {
      flatpickrInstance.setDate(parsedDate.toDate(), true)
      return
    }

    input.value = parsedDate.format("DD/MM/YYYY")
  }

  readAvailabilityDate () {
    const input = this.availabilityDateTarget
    if (!input) return ""

    const flatpickrInstance = input._flatpickr
    if (flatpickrInstance?.selectedDates?.length) {
      return flatpickrInstance.formatDate(flatpickrInstance.selectedDates[0], "d/m/Y")
    }

    return input.value
  }

  updateEndTimeFromDuration () {
    const start = moment(this.startTimeTarget.value, "HH:mm")
    if (!start.isValid()) return

    const appointmentsNumber = parseInt(this.appointmentsNumberTarget.value, 10) || 1
    const duration = this.appointmentDurationValue * appointmentsNumber

    this.endTimeTarget.value = start.add(duration, "minutes").format("HH:mm")
  }

  updateAvailabilityTitle () {
    this.availabilityModalTitleTarget.textContent = this.editing
      ? this.editAvailabilityTitleValue
      : this.newAvailabilityTitleValue
  }

  updateAvailabilityActions () {
    if (!this.hasSubmitAvailabilityButtonTarget) return

    this.submitAvailabilityButtonTarget.textContent = this.editing
      ? this.updateActionLabelValue
      : this.createActionLabelValue
    this.deleteAvailabilityButtonTarget.classList.toggle("d-none", !this.editing)
  }

  async submitAvailability () {
    if (!this.hasSubmitAvailabilityButtonTarget) return

    this.syncAvailabilityAttributes()

    const method = this.editing ? "PUT" : "POST"
    const url = this.editing
      ? `${AVAILABILITIES_ENDPOINT}/${this.currentAvailability.id}`
      : AVAILABILITIES_ENDPOINT

    const response = await fetch(url, {
      method,
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin",
      body: JSON.stringify({ availability: this.currentAvailability.attributes })
    })

    if (response.ok) {
      this.hideModal(this.availabilityModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(
        this.editing ? this.updatedFlashMessageValue : this.createdFlashMessageValue,
        "success"
      )
      return
    }

    const data = await response.json()
    this.currentAvailability = this.normalizeAvailabilityRecord(data.data)
    if (this.hasAvailabilityErrorsTarget) {
      this.displayErrors(this.availabilityErrorsTarget, this.extractRecordErrors(data))
    }
  }

  async deleteAvailability () {
    const response = await fetch(`${AVAILABILITIES_ENDPOINT}/${this.currentAvailability.id}`, {
      method: "DELETE",
      headers: {
        Accept: "application/json",
        "X-CSRF-Token": this.csrfToken
      },
      credentials: "same-origin"
    })

    if (response.ok) {
      this.hideModal(this.availabilityModalIdValue)
      this.calendar.refetchEvents()
      this.showFlash(this.deletedFlashMessageValue, "success")
      return
    }

    const data = await response.json()
    this.currentAvailability = this.normalizeAvailabilityRecord(data.data)
    if (this.hasAvailabilityErrorsTarget) {
      this.displayErrors(this.availabilityErrorsTarget, this.extractRecordErrors(data))
    }
  }

  extractRecordErrors (payload) {
    const record = payload?.data
    const attributes = Array.isArray(record) ? record[0]?.attributes : record?.attributes

    return attributes?.errors || []
  }

  normalizeAvailabilityRecord (record) {
    if (Array.isArray(record)) return record[0]

    return record
  }

  syncAvailabilityAttributes () {
    this.currentAvailability.attributes.date = this.readAvailabilityDate()
    this.currentAvailability.attributes.start_time = this.startTimeTarget.value
    this.currentAvailability.attributes.end_time = this.endTimeTarget.value
    this.currentAvailability.attributes.appointments_number = this.appointmentsNumberTarget.value
    this.currentAvailability.attributes.practitioner_office_id = this.practitionerOfficeIdTarget.value
    this.currentAvailability.attributes.practitioner_id = this.practitionerIdValue
  }

  newAvailabilityPayload () {
    return {
      id: null,
      attributes: {
        date: "",
        start_time: "",
        end_time: "",
        practitioner_office_id: "",
        appointments_number: 1,
        errors: []
      }
    }
  }

  hideModal (modalId) {
    const element = document.getElementById(modalId)
    const modal = element ? Modal.getInstance(element) : null
    if (modal) modal.hide()
  }

  showFlash (message, kind) {
    new FlashMessage(message, kind)
  }

  clearErrors (target) {
    target.innerHTML = ""
  }

  displayErrors (target, errors) {
    if (!errors?.length) {
      this.clearErrors(target)
      return
    }

    target.innerHTML = `
      <div class="alert alert-danger">
        <ul class="mb-0">
          ${errors.map((error) => `<li>${error}</li>`).join("")}
        </ul>
      </div>
    `
  }

  get csrfToken () {
    return document.querySelector("meta[name='csrf-token']")?.content
  }
}
